<template>
	<div class="container-fluid p-4">
		<div class="mb-3">
			<RouterLink
				to="/_/configuration/organization"
				class="text-decoration-none small"
			>
				<i class="bi bi-arrow-left me-1"></i>Back to district settings
			</RouterLink>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div v-if="loading" class="text-center p-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else-if="tag">
			<div class="d-flex align-items-center gap-2 mb-4">
				<span
					class="tag-swatch"
					:style="{ backgroundColor: tag.color }"
					aria-hidden="true"
				></span>
				<h1 class="mb-0">{{ tag.name }}</h1>
				<span v-if="tag.retired" class="badge text-bg-secondary">Retired</span>
			</div>

			<div class="row g-4">
				<div class="col-12 col-lg-7">
					<div class="card">
						<div class="card-body">
							<h2 class="h5 mb-3">The tag</h2>
							<div class="row g-3">
								<div class="col-12 col-md-6">
									<label class="form-label" for="detail-name">Name</label>
									<input
										id="detail-name"
										v-model="form.name"
										class="form-control"
									/>
								</div>
								<div class="col-12 col-md-6">
									<label class="form-label" for="detail-priority">
										Priority
									</label>
									<input
										id="detail-priority"
										v-model.number="form.priority"
										class="form-control"
										type="number"
										step="1"
										title="Higher wins: this is the rank the map applies among the tags a Site carries"
									/>
								</div>
								<div class="col-12">
									<label class="form-label" for="detail-description">
										Description
									</label>
									<textarea
										id="detail-description"
										v-model="form.description"
										class="form-control"
										rows="2"
									></textarea>
								</div>
								<div class="col-12 col-md-6">
									<label class="form-label" for="detail-color">Colour</label>
									<input
										id="detail-color"
										v-model="form.color"
										class="form-control form-control-color"
										type="color"
									/>
									<!-- §7.1: the tag's colour is its chip, swatch and legend
									     entry. The seeded symbol does not follow it. -->
									<p class="form-text mb-0">
										The map colour comes from the bound symbol.
									</p>
								</div>
								<div class="col-12 col-md-6">
									<label class="form-label" for="detail-slug">Slug</label>
									<input
										id="detail-slug"
										class="form-control"
										:value="tag.slug"
										readonly
									/>
									<p class="form-text mb-0">
										The slug is how the tag is filtered and referenced, and it
										never changes — renaming the tag does not re-derive it.
									</p>
								</div>
								<div class="col-12">
									<span class="form-label d-block">Icon</span>
									<IconUpload v-model="form.iconUuid" />
								</div>
							</div>
							<div class="d-flex gap-2 mt-3">
								<ButtonLoading
									text="Save"
									icon="bi bi-check-lg"
									variant="primary"
									:loading="saving"
									@click="save"
								/>
								<ButtonLoading
									text="Revert"
									variant="outline-secondary"
									:disabled="saving || !dirty"
									@click="resetForm"
								/>
							</div>
						</div>
					</div>
				</div>

				<div class="col-12 col-lg-5">
					<div class="card mb-4">
						<div class="card-body">
							<h2 class="h5 mb-3">Symbol</h2>

							<div class="d-flex align-items-center gap-2 mb-3">
								<span
									class="symbol-preview"
									:class="{ 'symbol-preview--none': !previewStyle }"
									:style="previewStyle ? styleCss(previewStyle) : undefined"
									aria-hidden="true"
								></span>
								<template v-if="resolvedStyle">
									<span class="fw-semibold">
										{{ resolvedStyle.name ?? "The product default" }}
									</span>
									<!-- The catalog's own `source`, never a re-derivation: it is
									     the arm that won (§4.2). -->
									<span class="badge" :class="sourceBadgeClass">
										{{ sourceLabel }}
									</span>
								</template>
								<span v-else class="text-body-secondary small">
									The catalog carries no style for this tag yet.
								</span>
							</div>

							<span class="form-label d-block">Draw this tag with</span>
							<SymbolPicker
								:model-value="selectedSymbolID"
								:symbols="districtSymbols"
								label-for-null="No district symbol bound"
								@update:model-value="bindSymbol"
							/>
							<p class="form-text">
								Only shared district symbols are offered: a binding is the
								district's answer, so it may not name a private symbol that
								would leave with its owner.
							</p>
							<p
								v-if="districtSymbols.length === 0"
								class="small text-body-secondary mb-0"
							>
								This district has no shared symbols yet.
								<RouterLink to="/_/configuration/organization">
									Create one in Site Symbology
								</RouterLink>
								and bind it here.
							</p>
							<p
								v-else-if="hiddenByOwnView"
								class="small text-body-secondary mb-0"
							>
								Your own override decides this tag's look, so the district's
								binding is not shown here — choosing a symbol below replaces it.
							</p>
						</div>
					</div>

					<div class="card">
						<div class="card-body">
							<h2 class="h5 mb-3">Usage</h2>
							<dl class="row mb-0 small">
								<dt class="col-7 fw-normal text-body-secondary">Used by</dt>
								<dd class="col-5 text-end mb-2">
									{{ (tag.site_count ?? 0).toLocaleString() }} Sites
								</dd>
								<dt class="col-7 fw-normal text-body-secondary">Leads on</dt>
								<dd class="col-5 text-end mb-2">
									{{ tag.leading_site_count.toLocaleString() }} Sites
								</dd>
								<dt class="col-7 fw-normal text-body-secondary">
									Shadowed by a higher-priority tag on
								</dt>
								<dd class="col-5 text-end mb-0">
									{{ tag.shadowed_site_count.toLocaleString() }} Sites
								</dd>
							</dl>
							<p class="form-text mb-0 mt-3">
								A Site carrying several tags draws with the highest-ranked tag
								the district has bound a symbol to; the Sites counted as
								shadowed carry this tag without it winning.
							</p>
						</div>
					</div>
				</div>
			</div>
		</template>

		<ToastNotification
			v-model:show="toast.show"
			:title="toast.title"
			:message="toast.message"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import IconUpload from "@/components/symbology/IconUpload.vue";
import SymbolPicker from "@/components/symbology/SymbolPicker.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { catalogTag, styleCss } from "@/lib/symbology";
import type { SiteTag, Symbol, SymbologyCatalog } from "@/type/api";

/** One tag as the detail read answers it: the tag's own row plus the two usage
 * readings the map's ranking produces (§8.2). */
interface SiteTagDetail extends SiteTag {
	leading_site_count: number;
	shadowed_site_count: number;
}

interface Props {
	id: number;
}
const props = defineProps<Props>();

const tag = ref<SiteTagDetail | null>(null);
const symbols = ref<Symbol[]>([]);
const catalog = ref<SymbologyCatalog | null>(null);
const loading = ref(true);
const saving = ref(false);
const binding = ref(false);
const error = ref("");
const selectedSymbolID = ref<number | null>(null);
const toast = reactive({ show: false, title: "", message: "" });

const form = reactive({
	name: "",
	description: "",
	color: "#2563eb",
	iconUuid: null as string | null,
	priority: 0,
});

/** The district's shared symbols only: the server refuses a private one on this
 * binding, so the picker must not offer one. */
const districtSymbols = computed(() =>
	symbols.value.filter((symbol) => symbol.owner === "district"),
);

/** The catalog's entry for this tag — arms 1/2 plus the kind's product default,
 * resolved server side. Its `source` is the arm that won; nothing here re-derives
 * the order. */
const tagStyle = computed(() =>
	catalog.value ? catalogTag(catalog.value, props.id) : undefined,
);

const resolvedStyle = computed(() => tagStyle.value?.style ?? null);

/** The symbol the district has bound to this tag: readable from the catalog only
 * while the district's own binding is the arm that wins. An override of the
 * viewer's own hides it, which the screen says rather than guessing. */
const boundSymbolID = computed(() => {
	const style = resolvedStyle.value;
	if (!style || style.source !== "district") return null;
	return style.symbol_id;
});

const hiddenByOwnView = computed(() => resolvedStyle.value?.source === "user");

/** The live preview: the bound symbol's own style when it is visible, otherwise
 * the resolved catalog style — which is what the map would draw. */
const previewStyle = computed(() => {
	if (!resolvedStyle.value) return null;
	const bound = symbols.value.find(
		(symbol) => symbol.id === boundSymbolID.value,
	);
	return bound ?? resolvedStyle.value;
});

const sourceLabel = computed(() => {
	switch (resolvedStyle.value?.source) {
		case "district":
			return "district";
		case "user":
			return "yours";
		default:
			return "built-in";
	}
});

const sourceBadgeClass = computed(() => {
	switch (resolvedStyle.value?.source) {
		case "district":
			return "text-bg-primary";
		case "user":
			return "text-bg-info";
		default:
			return "text-bg-secondary";
	}
});

const dirty = computed(() => {
	if (!tag.value) return false;
	return (
		form.name !== tag.value.name ||
		form.description !== tag.value.description ||
		form.color !== tag.value.color ||
		form.priority !== tag.value.priority ||
		form.iconUuid !== (tag.value.icon_uuid ?? null)
	);
});

function showToast(title: string, message: string) {
	toast.title = title;
	toast.message = message;
	toast.show = true;
}

function errorMessage(e: any): string {
	return e?.response?.data?.message || e?.message || "The request failed.";
}

function resetForm() {
	if (!tag.value) return;
	form.name = tag.value.name;
	form.description = tag.value.description;
	form.color = tag.value.color;
	form.priority = tag.value.priority;
	form.iconUuid = tag.value.icon_uuid ?? null;
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const [detail, list, resolved] = await Promise.all([
			apiClient.JSONGet<SiteTagDetail>(`/api/site-tag/${props.id}`),
			apiClient.JSONGet<Symbol[]>("/api/symbol?include_retired=1"),
			apiClient.JSONGet<SymbologyCatalog>("/api/symbology/catalog"),
		]);
		tag.value = detail;
		symbols.value = list;
		catalog.value = resolved;
		resetForm();
		selectedSymbolID.value = boundSymbolID.value;
	} catch (e: any) {
		error.value = errorMessage(e);
	} finally {
		loading.value = false;
	}
}

/** Re-read the catalog after a write: it is the only source of a tag's resolved
 * style, and the server rebuilds it from the rows that just changed. */
async function refreshStyles() {
	catalog.value = await apiClient.JSONGet<SymbologyCatalog>(
		"/api/symbology/catalog",
	);
	selectedSymbolID.value = boundSymbolID.value;
}

async function save() {
	if (saving.value) return;
	const name = form.name.trim();
	if (name === "") {
		showToast("Name required", "A tag needs a name.");
		return;
	}
	saving.value = true;
	try {
		await apiClient.JSONPut(`/api/site-tag/${props.id}`, {
			name,
			description: form.description,
			color: form.color,
			priority: form.priority,
			// The empty string clears the icon; omitting it would leave it alone.
			icon_uuid: form.iconUuid ?? "",
		});
		// The write answers the tag row alone; the usage readings only come from
		// the detail read, so take the row back through it.
		tag.value = await apiClient.JSONGet<SiteTagDetail>(
			`/api/site-tag/${props.id}`,
		);
		resetForm();
		await refreshStyles();
		showToast("Saved", `“${name}” is updated.`);
	} catch (e: any) {
		showToast("Could not save the tag", errorMessage(e));
	} finally {
		saving.value = false;
	}
}

/** The picker's write: a symbol binds through PUT, the empty choice unbinds
 * through DELETE. */
async function bindSymbol(symbolID: number | null) {
	if (binding.value) return;
	const previous = selectedSymbolID.value;
	selectedSymbolID.value = symbolID;
	binding.value = true;
	try {
		if (symbolID === null) {
			await apiClient.JSONDelete(`/api/site-tag/${props.id}/symbol`);
			showToast("Unbound", "This tag keeps the product default again.");
		} else {
			await apiClient.JSONPut(`/api/site-tag/${props.id}/symbol`, {
				symbol_id: symbolID,
			});
			showToast("Bound", "Sites this tag leads on now draw with that symbol.");
		}
		await refreshStyles();
	} catch (e: any) {
		showToast("Could not change the binding", errorMessage(e));
		// The picker's value is a local echo of a write that failed, so put the
		// catalog's answer back on screen.
		selectedSymbolID.value = previous;
	} finally {
		binding.value = false;
	}
}

watch(() => props.id, load, { immediate: true });
</script>

<style scoped>
.tag-swatch {
	display: inline-block;
	width: 1.25rem;
	height: 1.25rem;
	border-radius: 50%;
	border: 1px solid var(--bs-secondary-border-subtle);
}

.symbol-preview {
	display: inline-block;
	width: 2rem;
	height: 2rem;
	border-radius: 50%;
}

.symbol-preview--none {
	background: transparent;
	border: 1px dashed var(--bs-secondary-color);
}
</style>
